// ============================================================================
// BarBottle — one real bottle in OrderUp's platform bottle catalog
// ----------------------------------------------------------------------------
// Root collection `bar_bottles/{guid}`. Server-only in firestore.rules, read
// through the API — the same posture as `bar_catalog_items`.
//
// ── Why this exists ─────────────────────────────────────────────────────────
// A drink's build names ROLES ("Triple Sec"); a restaurant stocks BOTTLES
// ("Combier Liqueur d'Orange 750 ml"). Until a restaurant had inventory rows,
// every bottle picker on the Bar Menu page was empty — House of Pho had none,
// measured 2026-09-21 — and nothing gave them a way to get rows except typing
// each bottle into RecipeOps by hand.
//
// A restaurant CARRIES a catalog bottle: that creates one inventory item
// through the normal RecipeOps path (Firestore row + Books record + audit),
// stocked in the unit its role is poured in, and — if nothing already plays
// that role — makes it the house pour. Ticking twenty bottles fills every
// picker and starts every adopted drink depleting stock.
//
// ── Stocked in pour units, counted in bottles ───────────────────────────────
// A BOM line's unit MUST equal its inventory item's unit (Decision 7, force
// unit-equality). A bar buys bottles and pours ounces, so a bottle entered as
// "1 bottle" could never map to a "1 oz" pour. A carried bottle is therefore
// stocked in its role's POUR unit (`stockUnit`), and `containerSize` — that
// many pour units in one bottle — turns on-hand back into bottles for a human.
//
// ── What the catalog does NOT carry ─────────────────────────────────────────
// A distributor SKU. It differs per distributor and per state, so it belongs
// on the restaurant's own inventory item (`sku`), where the vendor item map
// (docs/superpowers/specs/2026-09-20-vendor-item-map-design.md) matches it to
// invoices. The catalog carries the UPC — printed on the bottle, the same
// everywhere — and only when it came from a verified source. A made-up
// barcode would match the wrong product on the first scan.
// ============================================================================

/** How the bottle sits on a bar's shelf. Drives ordering, never pricing. */
export type BarBottleTier = "well" | "call" | "premium" | "top_shelf";

export const BAR_BOTTLE_TIERS: readonly BarBottleTier[] = [
  "well",
  "call",
  "premium",
  "top_shelf",
] as const;

/**
 * The unit a carried bottle is stocked in. MUST be the unit its role is poured
 * in, or no drink can ever deplete it (Decision 7). Every role in the catalog
 * pours in exactly one unit — bitters and absinthe in dashes, everything a
 * bottle holds otherwise in ounces — which `barSeedData.test.ts` asserts.
 */
export type BarBottleStockUnit = "oz" | "dash" | "ea";

export const BAR_BOTTLE_STOCK_UNITS: readonly BarBottleStockUnit[] = [
  "oz",
  "dash",
  "ea",
] as const;

import { normalizeBarImageUrl } from "./barBottleImage";

/**
 * What kind of product a catalog row is. The curated rows are spirits,
 * liqueurs and a few wines (vermouth, prosecco); the imported rows add every
 * wine, beer and keg a state's product list carries. `mixer` is curated only
 * — the import never produces one (Part 4, design §5).
 *
 * It decides the STOCK UNIT of an imported row (`barBottleStockUnitFor`) and
 * is the filter on the Bottles tab. It never decides pricing.
 */
export type BarBottleCategory = "spirit" | "liqueur" | "wine" | "beer" | "keg" | "mixer";

export const BAR_BOTTLE_CATEGORIES: readonly BarBottleCategory[] = [
  "spirit",
  "liqueur",
  "wine",
  "beer",
  "keg",
  "mixer",
] as const;

export const BAR_BOTTLE_CATEGORY_LABELS: Readonly<Record<BarBottleCategory, string>> = {
  spirit: "Spirits",
  liqueur: "Liqueurs",
  wine: "Wine",
  beer: "Beer (bottles & cans)",
  keg: "Kegs",
  mixer: "Mixers",
};

/**
 * How a mixer reaches the glass — a category-`mixer` row only. `gun` and
 * `house` carry with depletion off by default (design §5); `packaged` is
 * counted by the each, like beer.
 */
export type BarMixerDispense = "gun" | "house" | "packaged";

export const BAR_MIXER_DISPENSES: readonly BarMixerDispense[] = [
  "gun",
  "house",
  "packaged",
] as const;

export const BAR_MIXER_DISPENSE_LABELS: Readonly<Record<BarMixerDispense, string>> = {
  gun: "Gun",
  house: "House-made",
  packaged: "Packaged",
};

/**
 * Platform ingredient categories whose roles are MIXERS. A restaurant's own
 * bottle playing one must say how it is served (`dispense`), so every own
 * mixer carries `barMixerDispense` and is counted — or not — exactly as a
 * catalog mixer is.
 */
export const BAR_MIXER_ROLE_CATEGORIES: readonly string[] = ["MIXER", "SWEETENER"] as const;

/** One line explaining what a dispense means for depletion tracking. */
export const BAR_MIXER_DISPENSE_HELP =
  "Gun and house-made mixers are not counted by default; packaged mixers are counted per can or bottle.";

/**
 * THE stock-unit rule — the one every writer of a catalog row goes through
 * (the seed, a request approval, a correction, the admin form).
 *
 * 1. A row that PLAYS a role, or is poured AS one (a role-less substitute such
 *    as Red Bull Sugarfree, which must be callable for "Energy Drink" without
 *    becoming its house pour), is stocked in that role's POUR unit
 *    (`opts.pourUnit`, from `barRoleStockUnitFrom`) — whatever its category.
 *    A BOM line's unit must equal its item's (Decision 7): a Fever-Tree
 *    counted by the can could never be depleted by a Moscow Mule's 4 oz top.
 *    It is still counted in cans through `containerSize`.
 * 2. Otherwise the kind decides. A mixer by how it is served: `packaged` by
 *    the each, `gun` and `house` by the ounce. Everything else as it always
 *    was: a can or bottle of beer by the each; spirits, liqueurs, wine and a
 *    keg (drawn by the ounce) by the ounce.
 */
export function barBottleStockUnitFor(
  category: BarBottleCategory,
  dispense?: BarMixerDispense,
  opts: { pourUnit?: BarBottleStockUnit } = {},
): BarBottleStockUnit {
  if (opts.pourUnit) return opts.pourUnit;
  if (category === "mixer") return dispense === "packaged" ? "ea" : "oz";
  return category === "beer" ? "ea" : "oz";
}

/**
 * The unit a ROLE is poured in — the `pourUnit` for `barBottleStockUnitFor`.
 *
 * `pourUnits` is every unit a depleting drink line pours the role in. A role
 * poured in exactly one takes it; one poured in two is REFUSED rather than
 * picked, because no single stock unit lets every drink deplete it. A role no
 * drink pours yet takes its ingredient category's unit: bitters in dashes,
 * everything else a bottle holds in ounces.
 */
export function barRoleStockUnitFrom(
  pourUnits: ReadonlySet<string> | undefined,
  ingredientCategory?: string,
): BarBottleStockUnit {
  if (pourUnits && pourUnits.size > 0) {
    if (pourUnits.size !== 1) {
      throw new Error(
        `This role is poured in ${[...pourUnits].join(" and ")} — a bottle needs exactly one stock unit`,
      );
    }
    const unit = [...pourUnits][0];
    if (unit !== "oz" && unit !== "dash") {
      throw new Error(`This role is poured in "${unit}", which no bottle is stocked in`);
    }
    return unit;
  }
  return ingredientCategory === "BITTERS" ? "dash" : "oz";
}

/** What each stock unit is called on screen — one table for every app. */
export const BAR_BOTTLE_STOCK_UNIT_LABELS: Readonly<Record<BarBottleStockUnit, string>> = {
  oz: "Ounces",
  dash: "Dashes",
  // Says WHAT is counted — the whole can or bottle — which a bare "Each"
  // does not; the same words on the admin form and in Add your own.
  ea: "Each (by the can or bottle)",
};

/**
 * Millilitres in one stock unit.
 *
 * `oz` is the US fluid ounce, exact. `dash` has NO standard — it is 1/8
 * teaspoon here (0.616 ml), the most common bar convention. It only sets how
 * fast a bitters bottle reads as empty on a count; nothing is charged by it.
 */
export const ML_PER_STOCK_UNIT: Readonly<Record<Exclude<BarBottleStockUnit, "ea">, number>> = {
  oz: 29.5735,
  dash: 0.616,
};

export interface IBarBottle {
  /**
   * Document id. Seeded bottles derive it from `code` (RFC 4122 v5), so a
   * re-seed is idempotent; admin-created bottles take a `randomUUID`.
   */
  readonly guid: string;
  /** Stable uppercase code, one per SIZE — `COMBIER_LIQUEUR_DORANGE_750`. */
  readonly code: string;
  readonly brand: string;
  readonly product: string;
  /**
   * The ROLE this bottle plays — the platform `ingredients` guid a drink's
   * spec line names. A bottle can still be CALLED for any role whose pour
   * unit matches; this is only the role it is filed under and becomes the
   * house pour for.
   *
   * ABSENT on an imported row. A state product list says "Bogle Pinot Noir",
   * never which drink it plays in, and guessing would make it the house pour
   * for a role it does not fill. A role-less bottle is carried, counted and
   * CALLED like any other; it simply never becomes a house pour on its own.
   */
  readonly platformIngredientGuid?: string;
  /** Denormalised role label, so the list renders without a join. */
  readonly ingredientName?: string;
  /**
   * A role this ROLE-LESS bottle is poured AS without playing it — Red Bull
   * Sugarfree is called for "Energy Drink" in a Vodka Red Bull, Diet Cola for
   * "Cola". It sets the stock unit (the role's pour unit, so the call-brand
   * picker can offer it) and makes it findable by the role, but it NEVER makes
   * the bottle a house pour: a diet variant carried first must not pour into
   * every drink. Never set together with `platformIngredientGuid`.
   */
  readonly pourAsIngredientGuid?: string;
  /** Denormalised label of `pourAsIngredientGuid`. */
  readonly pourAsName?: string;
  readonly category: BarBottleCategory;
  /** The source's own grouping — "Bourbon", "Cordials", "Pinot Noir". Display only. */
  readonly subcategory?: string;
  /**
   * Where an imported row came from, as `<source>:<item code>` — `md:171162`,
   * `or:0765B`. It is how a re-import finds the row it wrote last time, so a
   * re-run updates rather than duplicates. Absent on curated and admin rows.
   */
  readonly sourceKeys?: readonly string[];
  /** Bottle volume. Each size is its own catalog row: it is its own product. */
  readonly sizeMl: number;
  readonly stockUnit: BarBottleStockUnit;
  readonly tier: BarBottleTier;
  /** How a mixer reaches the glass. Present on `category: 'mixer'` rows only. */
  readonly dispense?: BarMixerDispense;
  /** From a verified source only. Absent is honest; a guess is not. */
  readonly proof?: number;
  /** From a verified source only. Absent is honest; a guess is not. */
  readonly upc?: string;
  /**
   * The catalog's PHOTO of this bottle (design 2026-10-05 Part 1): a download
   * URL in platform storage (`BAR_CATALOG_PHOTO_STORAGE_PREFIX`), written ONLY
   * by Site Admin — an upload, or an approved photo offer. Absent = no photo.
   * A restaurant's own photo outranks it (`resolveBarBottleImage`).
   */
  readonly imageUrl?: string;
  /** Retired bottles stay carried where they are carried; they stop listing. */
  readonly active: boolean;
  /**
   * Set on a row RETIRED BY A MERGE: the row that replaced it
   * (`barBottleDedupe.ts`). A re-import that finds this row by its source key
   * follows the link instead of reviving or re-splitting it, and a reader
   * holding the old guid (a sale, a count, an audit — never rewritten) can
   * still name today's row. Absent on every other row.
   */
  readonly mergedIntoGuid?: string;
  readonly publishedBy: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

const toDate = (value: unknown): Date => {
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate: () => Date }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (value instanceof Date) return value;
  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
};

export class BarBottle implements IBarBottle {
  readonly guid: string;
  readonly code: string;
  readonly brand: string;
  readonly product: string;
  readonly platformIngredientGuid?: string;
  readonly ingredientName?: string;
  readonly pourAsIngredientGuid?: string;
  readonly pourAsName?: string;
  readonly category: BarBottleCategory;
  readonly subcategory?: string;
  readonly sourceKeys?: readonly string[];
  readonly sizeMl: number;
  readonly stockUnit: BarBottleStockUnit;
  readonly tier: BarBottleTier;
  readonly dispense?: BarMixerDispense;
  readonly proof?: number;
  readonly upc?: string;
  readonly imageUrl?: string;
  readonly active: boolean;
  readonly mergedIntoGuid?: string;
  readonly publishedBy: string;
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(data: IBarBottle) {
    this.guid = data.guid;
    this.code = data.code;
    this.brand = data.brand;
    this.product = data.product;
    this.platformIngredientGuid = data.platformIngredientGuid;
    this.ingredientName = data.ingredientName;
    this.pourAsIngredientGuid = data.pourAsIngredientGuid;
    this.pourAsName = data.pourAsName;
    this.category = data.category;
    this.subcategory = data.subcategory;
    this.sourceKeys = data.sourceKeys;
    this.sizeMl = data.sizeMl;
    this.stockUnit = data.stockUnit;
    this.tier = data.tier;
    this.dispense = data.dispense;
    this.proof = data.proof;
    this.upc = data.upc;
    this.imageUrl = data.imageUrl;
    this.active = data.active;
    this.mergedIntoGuid = data.mergedIntoGuid;
    this.publishedBy = data.publishedBy;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  static fromJSON(data: Record<string, unknown>): BarBottle {
    const stockUnit = String(data.stockUnit ?? "oz") as BarBottleStockUnit;
    const tier = String(data.tier ?? "call") as BarBottleTier;
    const category = String(data.category ?? "spirit") as BarBottleCategory;
    const dispense = data.dispense != null ? String(data.dispense) : undefined;
    const role = data.platformIngredientGuid ? String(data.platformIngredientGuid) : "";
    const pourAs = data.pourAsIngredientGuid ? String(data.pourAsIngredientGuid) : "";
    const sourceKeys = Array.isArray(data.sourceKeys)
      ? data.sourceKeys.map(String).filter(Boolean)
      : [];
    return new BarBottle({
      guid: String(data.guid ?? ""),
      code: String(data.code ?? ""),
      brand: String(data.brand ?? ""),
      product: String(data.product ?? ""),
      // Empty and absent are the same thing — no role — so neither reaches a
      // caller as a guid that matches nothing.
      platformIngredientGuid: role || undefined,
      ingredientName: role && data.ingredientName ? String(data.ingredientName) : undefined,
      // Only meaningful on a row with no role of its own.
      pourAsIngredientGuid: !role && pourAs ? pourAs : undefined,
      pourAsName: !role && pourAs && data.pourAsName ? String(data.pourAsName) : undefined,
      // Rows seeded before categories existed are all spirits or liqueurs;
      // `spirit` is the honest default until a re-seed files them.
      category: BAR_BOTTLE_CATEGORIES.includes(category) ? category : "spirit",
      subcategory: data.subcategory ? String(data.subcategory) : undefined,
      sourceKeys: sourceKeys.length > 0 ? sourceKeys : undefined,
      sizeMl: Number(data.sizeMl ?? 0),
      // An unknown unit falls back to `oz` rather than reaching the carry path
      // as a string no BOM line will ever equal.
      stockUnit: BAR_BOTTLE_STOCK_UNITS.includes(stockUnit) ? stockUnit : "oz",
      tier: BAR_BOTTLE_TIERS.includes(tier) ? tier : "call",
      // Only meaningful on a mixer row; an unknown or stray value on any
      // other category is dropped rather than reaching a caller as a string
      // no `BarMixerDispense` will ever equal.
      dispense:
        category === "mixer" && dispense && BAR_MIXER_DISPENSES.includes(dispense as BarMixerDispense)
          ? (dispense as BarMixerDispense)
          : undefined,
      proof: data.proof != null ? Number(data.proof) : undefined,
      upc: data.upc != null && String(data.upc) !== "" ? String(data.upc) : undefined,
      // Only a usable URL is a photo — anything else reads as none.
      imageUrl: normalizeBarImageUrl(data.imageUrl),
      active: data.active !== false,
      mergedIntoGuid: data.mergedIntoGuid ? String(data.mergedIntoGuid) : undefined,
      publishedBy: String(data.publishedBy ?? ""),
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
    });
  }

  toJSON(): IBarBottle {
    const json = { ...this } as unknown as Record<string, unknown>;
    // An absent optional must not reach Firestore as `undefined`.
    for (const key of Object.keys(json)) if (json[key] === undefined) delete json[key];
    return json as unknown as IBarBottle;
  }
}

/** "750 ml", "1 L", "1.75 L", "118 ml". */
/**
 * Standard US keg volumes, in ml, as a keg is named on an invoice. A keg is
 * 58.67 L to nobody; it is a half barrel.
 */
export const BAR_KEG_SIZES_ML: Readonly<Record<string, number>> = {
  "1/2 bbl": 58674,
  "1/4 bbl": 29337,
  "1/6 bbl": 19558,
};

/**
 * A soda gun's bag-in-box syrup, as it is bought: 5 US gallons. 18.93 L is
 * what it holds to nobody. Mixer rows only — nothing else comes in this size.
 */
export const BAR_BAG_IN_BOX_SIZES_ML: Readonly<Record<string, number>> = {
  "5 gal": 18927,
};

export function formatBarBottleSize(sizeMl: number): string {
  for (const [name, ml] of [
    ...Object.entries(BAR_KEG_SIZES_ML),
    ...Object.entries(BAR_BAG_IN_BOX_SIZES_ML),
  ]) {
    if (Math.round(sizeMl) === ml) return name;
  }
  if (sizeMl >= 1000) {
    return `${Number((sizeMl / 1000).toFixed(2))} L`;
  }
  return `${Math.round(sizeMl)} ml`;
}

/** "Combier Liqueur d'Orange" — brand and product, without repeating the brand. */
export function barBottleDisplayName(
  bottle: Pick<IBarBottle, "brand" | "product">,
): string {
  const brand = bottle.brand.trim();
  const product = bottle.product.trim();
  if (!brand) return product;
  if (!product || product.toLowerCase().startsWith(brand.toLowerCase())) {
    return product || brand;
  }
  return `${brand} ${product}`;
}

/**
 * What the carried inventory item is called — and so what every bottle
 * picker, the build card and a stock count show: "Combier Liqueur d'Orange
 * 750 ml". The size is part of the name because a 750 and a 1.75 of the same
 * spirit are two different things on a shelf and on an invoice.
 */
export function barBottleLabel(
  bottle: Pick<IBarBottle, "brand" | "product" | "sizeMl">,
): string {
  return `${barBottleDisplayName(bottle)} ${formatBarBottleSize(bottle.sizeMl)}`;
}

/**
 * Pour units in one bottle — 750 ml is 25.36 oz. Rounded to 2 places, which is
 * finer than any bar measures and keeps a count readable.
 */
export function barBottleContainerSize(
  sizeMl: number,
  stockUnit: BarBottleStockUnit,
): number {
  if (!(sizeMl > 0)) return 0;
  // A can or bottle of beer is counted whole: one container IS one unit.
  if (stockUnit === "ea") return 1;
  const perUnit = ML_PER_STOCK_UNIT[stockUnit];
  if (!perUnit) return 0;
  return Number((sizeMl / perUnit).toFixed(2));
}

/**
 * On-hand as a person counts it: "3.5 bottles", never "88.76 oz". Returns
 * undefined when the item has no container size, so a caller shows the raw
 * quantity instead of dividing by zero.
 */
export function barBottlesOnHand(
  onHandQty: number,
  containerSize: number | undefined | null,
): number | undefined {
  if (!containerSize || containerSize <= 0) return undefined;
  return Number((onHandQty / containerSize).toFixed(2));
}

// ── Carrying ────────────────────────────────────────────────────────────────

/**
 * What makes two catalog rows the SAME product in different sizes: brand,
 * product and the role it plays. Each size is still its own row — a 750 ml and
 * a 1 L carry different barcodes — but a restaurant thinks of "Combier" once and
 * picks the size on its shelf, which is how the Bottles tab shows them.
 */
export function barBottleProductKey(
  bottle: Pick<IBarBottle, "brand" | "product" | "platformIngredientGuid">,
): string {
  return [
    bottle.brand.trim().toLowerCase(),
    bottle.product.trim().toLowerCase(),
    bottle.platformIngredientGuid ?? "",
  ].join("|");
}

/**
 * One bottle to carry. `sizeMl` is the size on the restaurant's shelf when the
 * catalog has no row for it — House of Pho pours Combier from a 1 L the catalog
 * first listed only as 750 ml, and no restaurant should wait on OrderUp to add a
 * size before it can count what it buys. Absent = the row's own size.
 */
export interface IBarCarryRequest {
  readonly bottleGuid: string;
  readonly sizeMl?: number;
}

/** A live inventory item, as the carry planner reads it. */
export interface IBarCarriedItem {
  readonly inventoryGuid: string;
  readonly catalogBottleGuid?: string;
  /** Set when this item is the house pour for that role. */
  readonly platformIngredientGuid?: string;
  /**
   * The bottle size actually carried. Absent on items carried before a size
   * could differ from its catalog row — read as that row's own size.
   */
  readonly bottleSizeMl?: number;
}

export interface IBarCarryPlanRow {
  /** The catalog row this item links to — the product, and its barcode when sizes match. */
  readonly bottle: IBarBottle;
  /** The size the restaurant actually stocks. Equals `bottle.sizeMl` unless it chose another. */
  readonly sizeMl: number;
  /** Make it the house pour: nothing live plays this role yet. */
  readonly housePour: boolean;
  /** `barCarryDefaults(bottle).barSpiritType` — written onto the inventory item. */
  readonly barSpiritType?: BarSpiritType;
  /** `barCarryDefaults(bottle).trackDepletion` — `false` for a gun or house mixer, else absent. */
  readonly trackDepletion?: false;
}

/**
 * What carrying `bottle` stamps on the new inventory item beyond its name,
 * unit and size — both decided HERE, once, so the catalog carry and any other
 * caller cannot disagree:
 *
 *  - `trackDepletion: false` for a `gun` or `house` mixer (design §5).
 *    Counting soda-gun ounces is noise most bars do not want; the restaurant
 *    switches counting on per mixer on the Bottles tab. ABSENT otherwise —
 *    absent means "track it", which is what every item did before the field
 *    existed, and a packaged mixer is counted by the can like beer.
 *  - `barSpiritType` for a spirit or liqueur (`barSpiritTypeFor`), so a spirit
 *    list and an order-close substitution never need a catalog read. Wine,
 *    beer, kegs and mixers get none — a mixer called "Vodka Soda Mix" is
 *    still a mixer.
 */
export function barCarryDefaults(
  bottle: Pick<
    IBarBottle,
    "category" | "dispense" | "subcategory" | "ingredientName" | "product"
  >,
): { barSpiritType?: BarSpiritType; trackDepletion?: false } {
  const out: { barSpiritType?: BarSpiritType; trackDepletion?: false } = {};
  if (bottle.category === "mixer" && (bottle.dispense === "gun" || bottle.dispense === "house")) {
    out.trackDepletion = false;
  }
  if (bottle.category === "spirit" || bottle.category === "liqueur") {
    const spiritType = barSpiritTypeFor(bottle);
    if (spiritType) out.barSpiritType = spiritType;
  }
  return out;
}

export interface IBarCarryPlan {
  /** Bottles to create inventory items for, in request order. */
  readonly create: readonly IBarCarryPlanRow[];
  /** Requested bottle-and-size pairs already carried here — a no-op, not an error. */
  readonly alreadyCarried: readonly string[];
  /** Requested guids that are not in the live catalog. */
  readonly unknown: readonly string[];
}

/** The identity of one carried bottle: this catalog row, at this size. */
export const barCarryKey = (bottleGuid: string, sizeMl: number): string =>
  `${bottleGuid}@${Math.round(sizeMl)}`;

/**
 * Decide what carrying `requested` bottles does at one restaurant.
 *
 * Three rules, each of which fails silently if broken:
 *  - A bottle ALREADY carried AT THAT SIZE is skipped. A second inventory row
 *    for one bottle splits its count and cost across two items nobody can
 *    reconcile. The same product in a DIFFERENT size is a different item — a
 *    bar that stocks both a 750 and a 1 L counts them separately.
 *  - A size that has its OWN catalog row goes to that row, even when asked for
 *    as "another size" of a sibling. Each row is its own barcode; linking a
 *    1 L bottle to the 750 ml row would match the wrong product the day
 *    barcodes are scanned, and would let "Other size: 1 L" duplicate a 1 L the
 *    restaurant already carries.
 *  - A bottle becomes the HOUSE POUR only when nothing live plays its role —
 *    and only the FIRST such bottle in one request. Two bottles claiming one
 *    role is exactly what `mapInventoryIngredients` refuses.
 */
export function planBarBottleCarry(
  requested: readonly (string | IBarCarryRequest)[],
  catalog: ReadonlyMap<string, IBarBottle>,
  carried: readonly IBarCarriedItem[],
): IBarCarryPlan {
  const carriedKeys = new Set<string>();
  for (const c of carried) {
    if (!c.catalogBottleGuid) continue;
    const size = c.bottleSizeMl ?? catalog.get(c.catalogBottleGuid)?.sizeMl;
    if (size) carriedKeys.add(barCarryKey(c.catalogBottleGuid, size));
  }
  const rolesTaken = new Set(
    carried.map((c) => c.platformIngredientGuid).filter((g): g is string => !!g),
  );

  // Live rows by product and size, so an "other size" that exists as its own
  // row is carried AS that row.
  const rowByProductSize = new Map<string, IBarBottle>();
  for (const b of catalog.values()) {
    if (!b.active) continue;
    rowByProductSize.set(`${barBottleProductKey(b)}@${Math.round(b.sizeMl)}`, b);
  }

  const create: IBarCarryPlanRow[] = [];
  const alreadyCarried: string[] = [];
  const unknown: string[] = [];
  const seen = new Set<string>();

  for (const raw of requested) {
    const req: IBarCarryRequest = typeof raw === "string" ? { bottleGuid: raw } : raw;
    const asked = catalog.get(req.bottleGuid);
    if (!asked || !asked.active) {
      unknown.push(req.bottleGuid);
      continue;
    }
    const sizeMl = req.sizeMl && req.sizeMl > 0 ? req.sizeMl : asked.sizeMl;
    const bottle =
      rowByProductSize.get(`${barBottleProductKey(asked)}@${Math.round(sizeMl)}`) ?? asked;

    const key = barCarryKey(bottle.guid, sizeMl);
    if (seen.has(key)) continue;
    seen.add(key);
    if (carriedKeys.has(key)) {
      alreadyCarried.push(key);
      continue;
    }
    // A bottle with no role is never a house pour — there is nothing for it
    // to be the house pour OF.
    const role = bottle.platformIngredientGuid;
    const housePour = !!role && !rolesTaken.has(role);
    if (housePour && role) rolesTaken.add(role);
    create.push({ bottle, sizeMl, housePour, ...barCarryDefaults(bottle) });
  }

  return { create, alreadyCarried, unknown };
}

/**
 * Who inherits the house pour when a restaurant stops carrying the bottle
 * that held it: another live carried bottle filed under the SAME role, first
 * by the order given. Undefined when there is none — the role goes unmapped,
 * which the Bottle mapping tab reports.
 *
 * Without this, stopping the one bottle a role hung on would silently switch
 * every drink using that role back to depleting nothing, even though the
 * restaurant still stocks a bottle that plays it.
 */
export function planHousePourHeir(
  leaving: IBarCarriedItem,
  remaining: readonly IBarCarriedItem[],
  catalog: ReadonlyMap<string, IBarBottle>,
): string | undefined {
  const role = leaving.platformIngredientGuid;
  if (!role) return undefined;
  const heir = remaining.find((item) => {
    if (item.inventoryGuid === leaving.inventoryGuid) return false;
    if (item.platformIngredientGuid) return false;
    const bottle = item.catalogBottleGuid
      ? catalog.get(item.catalogBottleGuid)
      : undefined;
    return bottle?.platformIngredientGuid === role;
  });
  return heir?.inventoryGuid;
}

// ── Search ──────────────────────────────────────────────────────────────────

/**
 * The words of a bottle's name as search sees them: lower case, accents
 * dropped ("Patrón" finds "patron"), apostrophes closed up ("Jack Daniel's"
 * finds "daniels"), everything else a separator.
 */
export function barBottleSearchWords(text: string): string[] {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['\u2019`]/g, "")
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 0);
}

/** Prefixes are indexed up to this length; a longer query word is cut to it. */
export const BAR_BOTTLE_SEARCH_PREFIX_MAX = 12;

/**
 * The `searchTokens` stored on each catalog row: every prefix (2 characters
 * up) of every word of its brand, product and subcategory.
 *
 * The catalog holds tens of thousands of rows and Firestore has no text
 * search, so the API looks a query up with ONE `array-contains` on this field
 * — a single-field index Firestore keeps automatically, so no composite index
 * has to be deployed — and checks the remaining words in memory
 * (`barBottleMatchesSearch`). Prefixes, not whole words, so "tanq" finds
 * Tanqueray while someone is still typing.
 */
export function barBottleSearchTokens(
  bottle: Pick<IBarBottle, "brand" | "product" | "subcategory" | "ingredientName" | "pourAsName">,
): string[] {
  const tokens = new Set<string>();
  const words = barBottleSearchWords(
    [
      bottle.brand,
      bottle.product,
      bottle.subcategory ?? "",
      bottle.ingredientName ?? "",
      bottle.pourAsName ?? "",
    ].join(" "),
  );
  for (const word of words) {
    const max = Math.min(word.length, BAR_BOTTLE_SEARCH_PREFIX_MAX);
    for (let n = Math.min(2, word.length); n <= max; n++) tokens.add(word.slice(0, n));
  }
  return [...tokens].sort();
}

/**
 * The one token the API queries a search by: the most selective query word,
 * which is the longest, cut to the indexed prefix length. Undefined when the
 * query has no word of two or more characters — too broad to look up.
 */
export function barBottleSearchKey(query: string): string | undefined {
  const words = barBottleSearchWords(query).filter((w) => w.length >= 2);
  if (words.length === 0) return undefined;
  const longest = words.reduce((a, b) => (b.length > a.length ? b : a));
  return longest.slice(0, BAR_BOTTLE_SEARCH_PREFIX_MAX);
}

/**
 * Every query word starts some word of the bottle's name, its role ("triple
 * sec" finds Combier), its subcategory, or its size ("750").
 */
export function barBottleMatchesSearch(
  bottle: Pick<
    IBarBottle,
    "brand" | "product" | "subcategory" | "ingredientName" | "pourAsName" | "sizeMl"
  >,
  query: string,
): boolean {
  const needles = barBottleSearchWords(query);
  if (needles.length === 0) return true;
  const hay = barBottleSearchWords(
    [
      bottle.brand,
      bottle.product,
      bottle.subcategory ?? "",
      bottle.ingredientName ?? "",
      bottle.pourAsName ?? "",
      String(Math.round(bottle.sizeMl)),
    ].join(" "),
  );
  return needles.every((n) => hay.some((w) => w.startsWith(n)));
}

/**
 * The derived fields every WRITE of a catalog row stores beside it: the search
 * tokens, and the product key a carry looks a row's other sizes up by. Stored
 * rather than computed at read time because the catalog is too large to read
 * whole — every writer (the API, the seed, the import) calls this, so the
 * fields never disagree with the name they were derived from.
 */
export function barBottleIndexFields(
  bottle: Pick<
    IBarBottle,
    "brand" | "product" | "subcategory" | "ingredientName" | "pourAsName" | "platformIngredientGuid"
  >,
): { searchTokens: string[]; productKey: string } {
  return {
    searchTokens: barBottleSearchTokens(bottle),
    productKey: barBottleProductKey(bottle),
  };
}

// ── Spirit type ──────────────────────────────────────────────────────────────

/**
 * The spirit family a bottle pours as, for spirit-list generation (design
 * §6.1) and `InventoryItemOperational.barSpiritType` (Decision — resolver
 * substitution needs no catalog read at order close). Liqueurs get their own
 * value rather than folding into one of the base spirits, so a Liqueur list
 * can be generated too.
 */
export type BarSpiritType =
  | "vodka"
  | "rum"
  | "tequila"
  | "gin"
  | "bourbon"
  | "whiskey"
  | "scotch"
  | "cognac"
  | "liqueur";

export const BAR_SPIRIT_TYPES: readonly BarSpiritType[] = [
  "vodka",
  "rum",
  "tequila",
  "gin",
  "bourbon",
  "whiskey",
  "scotch",
  "cognac",
  "liqueur",
] as const;

export const BAR_SPIRIT_TYPE_LABELS: Readonly<Record<BarSpiritType, string>> = {
  vodka: "Vodka",
  rum: "Rum",
  tequila: "Tequila",
  gin: "Gin",
  bourbon: "Bourbon",
  whiskey: "Whiskey",
  scotch: "Scotch",
  cognac: "Cognac",
  liqueur: "Liqueur",
};

/** Lower case, accents dropped, spaces kept — so a two-word phrase like "single malt" still matches as a phrase. */
function normalizeSpiritWords(...parts: (string | undefined)[]): string {
  return ` ${parts.filter((p): p is string => !!p).join(" ")} `
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * First hit wins, checked against `ingredientName`, `subcategory` and
 * `product`: `liqueur` category always wins (a Liqueur list is what it fills);
 * then vodka, rum/rhum/cachaça, tequila/mezcal, gin, bourbon, scotch/single
 * malt/blended malt, cognac/brandy/armagnac, whiskey/whisky/rye. Bourbon and
 * scotch are checked BEFORE whiskey — "Maker's Mark Bourbon Whisky" is a
 * bourbon, not a whiskey, and "Glenlivet 12 Single Malt Scotch" is a scotch,
 * not a whiskey. Undefined for anything else (wine, beer, kegs, mixers, and a
 * spirit this list does not name) — nothing invents a family for a bottle
 * that has none.
 */
const SPIRIT_TYPE_RULES: readonly (readonly [RegExp, BarSpiritType])[] = [
  [/\bvodka\b/, "vodka"],
  [/\b(rum|rhum|cachaca)\b/, "rum"],
  [/\b(tequila|mezcal)\b/, "tequila"],
  [/\bgin\b/, "gin"],
  [/\bbourbon\b/, "bourbon"],
  [/\b(scotch|single malt|blended malt)\b/, "scotch"],
  [/\b(cognac|brandy|armagnac)\b/, "cognac"],
  [/\b(whiskey|whisky|rye)\b/, "whiskey"],
];

export function barSpiritTypeFor(
  b: Pick<IBarBottle, "category" | "subcategory" | "ingredientName" | "product">,
): BarSpiritType | undefined {
  if (b.category === "liqueur") return "liqueur";
  const text = normalizeSpiritWords(b.ingredientName, b.subcategory, b.product);
  for (const [re, type] of SPIRIT_TYPE_RULES) {
    if (re.test(text)) return type;
  }
  return undefined;
}

/**
 * The spirit family of a bottle the restaurant added itself (no catalog row).
 * Its ROLE — the platform ingredient it plays — decides whether it is a spirit
 * at all: only a `SPIRIT` or `LIQUEUR` role has a family, so a house syrup
 * named "Bourbon Barrel Syrup" is never filed as a bourbon. Then
 * `barSpiritTypeFor` reads the role's name and the bottle's own name. Used
 * when an own bottle is added and when spirit-list regeneration backfills one
 * carried before spirit types existed, so the two always agree.
 */
export function barSpiritTypeForRole(
  role: { readonly category?: string | null; readonly name?: string | null } | undefined,
  productName: string,
): BarSpiritType | undefined {
  const category: BarBottleCategory | undefined =
    role?.category === "SPIRIT" ? "spirit" : role?.category === "LIQUEUR" ? "liqueur" : undefined;
  if (!category) return undefined;
  return barSpiritTypeFor({ category, ingredientName: role?.name ?? undefined, product: productName });
}

/**
 * The spirit family of a bottle the restaurant added itself, from what the
 * ITEM knows. A dispense makes it a mixer: no family. A role, when known,
 * decides (`barSpiritTypeForRole`) — a juice role never becomes a vodka
 * whatever the bottle is called. With NO role (an own bottle that is not its
 * role's house pour keeps none on its item), its own name decides: "…
 * Liqueur" files as a liqueur, anything else is read as a spirit, and
 * `barSpiritTypeFor` finds the family in the name or returns nothing. Used
 * when an own bottle is added and when regeneration backfills one.
 */
export function barSpiritTypeForOwnBottle(bottle: {
  readonly name: string;
  readonly dispense?: string | null;
  readonly role?: { readonly category?: string | null; readonly name?: string | null };
}): BarSpiritType | undefined {
  if (bottle.dispense) return undefined;
  if (bottle.role) return barSpiritTypeForRole(bottle.role, bottle.name);
  const category: BarBottleCategory = /\bliqueur\b/i.test(bottle.name) ? "liqueur" : "spirit";
  return barSpiritTypeFor({ category, product: bottle.name });
}

/**
 * The catalog KIND a restaurant's own bottle is stocked like, when it is a
 * bottle poured by the measure: its role's category (`SPIRIT` / `LIQUEUR` /
 * `WINE`), or — for a role the platform does not know — the spirit family its
 * name gives it (`barSpiritTypeForOwnBottle`). `undefined` for a mixer (it has
 * a dispense), a beer, bitters or anything else: those keep the unit they
 * were added with. Such a bottle's unit is `barBottleStockUnitFor(kind, …)` —
 * the rule every catalog row of that kind goes through ("Add your own" and the
 * one-off repair of Jagan Vodka, stored by the each on 2026-10-03, both read it).
 */
export function ownBarBottleKind(
  roleCategory: string | null | undefined,
  barSpiritType: BarSpiritType | null | undefined,
  dispense: BarMixerDispense | null | undefined,
): Extract<BarBottleCategory, "spirit" | "liqueur" | "wine"> | undefined {
  if (dispense) return undefined;
  if (roleCategory === "SPIRIT") return "spirit";
  if (roleCategory === "LIQUEUR") return "liqueur";
  if (roleCategory === "WINE") return "wine";
  if (!roleCategory && barSpiritType) return barSpiritType === "liqueur" ? "liqueur" : "spirit";
  return undefined;
}
